





















































































































































































































































































































































import { Vue, Component, Mixins, Provide } from "vue-property-decorator";
import { Getter, namespace, State } from "vuex-class";

import NavHeader from "@/components/NavHeader.vue";
import SingleDeviceSearch from "@/components/video-reporting/SingleDeviceSearch.vue";
import BulkDeviceSearch from "@/components/video-reporting/BulkDeviceSearch.vue";
import CameraSearchResults from "@/components/video-reporting/CameraSearchResults.vue";
import Sessions from "@/components/Sessions.vue";
import SureViewIcon from "@/components/SureViewIcon.vue";

import { validationMixin } from "vuelidate";
import { required, requiredIf, between, numeric, minLength, maxLength } from "vuelidate/lib/validators";
import { noSpecialCharsExclDash } from "@/mixins/StringValidators";

import { UserPermissions } from "@/store/types";
import { VideoReportRequest, VideoReportingStates } from "@/store/video-reporting/types";
import videoReportingApi from "../services/api.videoReporting";
import { stringMixin } from "@/mixins";
import ReportFilters from "@/components/reports/ReportFilters.vue";

const VideoReportingStore = namespace("videoReporting");

@Component({
	components: {
		"nav-header": NavHeader,
		"single-device-search": SingleDeviceSearch,
		"bulk-device-search": BulkDeviceSearch,
		"camera-search-results": CameraSearchResults,
		sessions: Sessions,
		"sureview-icon": SureViewIcon,
	},
	validations: {
		videoReportRequest: {
			videoReportId: {
				minLength: minLength(1),
				maxLength: maxLength(256),
				required,
				noSpecialCharsExclDash,
			},
			cameraSettings: {
				maxCameras: {
					required,
					numeric,
					between: function (value) {
						// @ts-ignore
						return between(1, this.maxCamerasLimit)(value);
					},
				},
				maxDistance: {
					required: requiredIf(function () {
						return this.searchingNearbyCameras;
					}),
					numeric,
					between: function (value) {
						// @ts-ignore
						return between(this.searchingNearbyCameras ? 1 : 0, this.maxDistanceLimit)(value);
					},
				},
				maxDuration: {
					required,
					numeric,
					between: function (value) {
						// @ts-ignore
						return between(1, this.maxDurationLimit)(value);
					},
				},
			},
			search: {
				required,
			},
		},
	},
})
export default class VideoReporting extends Mixins(validationMixin) {
	@Getter("getPermissions") private userPermissions: UserPermissions;
	@Getter private getFeature: (feature: string[]) => boolean;

	@VideoReportingStore.State("videoReportingState") private currentState: VideoReportingStates;
	@VideoReportingStore.State("currentReport") private currentReport: {
		eventId: number | null;
		videoReportId: number | null;
		reviewsComplete: number;
		camerasComplete: number;
	};

	@VideoReportingStore.Getter("getMaxCamerasPref") private maxCamerasLimit: number;
	@VideoReportingStore.Getter("getMaxDistancePref") private maxDistanceLimit: number;
	@VideoReportingStore.Getter("getMaxDurationPref") private maxDurationLimit: number;

	@VideoReportingStore.Getter("getCurrentReportEventId") private currentReportEventId: number;
	@VideoReportingStore.Getter("getReviewsComplete") private reviewsComplete: number;

	@VideoReportingStore.Mutation private setVideoReportingState: (state: VideoReportingStates) => void;
	@VideoReportingStore.Mutation private resetState: () => void;

	@VideoReportingStore.Action private fetchVideoReportingPrefs: () => Promise<any>;
	@VideoReportingStore.Action private processReport: (request: VideoReportRequest) => Promise<VideoReportRequest>;

	public showCompleteReportModalFlag: boolean = false;
	private cameraSearch: "both" | "nearby" | "linked" = "both";

	/**
	 * Represents the video report the user is currently working on.
	 */
	private videoReportRequest: VideoReportRequest = {
		videoReportId: null,
		cameraSettings: {
			includeLinkedCameras: false,
			maxCameras: null,
			maxDistance: null,
			maxDuration: null,
		},
		search: null,
	};

	/**
	 * Returns true if the current user has permission to perform a bulk search.
	 */
	public get canUserBulkSearch() {
		return this.userPermissions.canPerformVideoReportUpload;
	}

	/**
	 * Returns true if the video reporting feature is enabled, and the user has permission to use it.
	 */
	public get isVideoReportingEnabled() {
		return this.getFeature(["VideoReporting"]) && this.userPermissions.canPerformVideoReport;
	}

	/**
	 * Gets the total number of device reviews within this report.
	 */
	public get totalReviewCount() {
		return this.videoReportRequest.search?.length ?? 0;
	}

	/**
	 * Returns the progress % value for report completion.
	 */
	public get reviewProgressValue() {
		if (this.currentState != VideoReportingStates.ProcessingReport) {
			return 0;
		}

		let totalCameraCount = this.videoReportRequest.search?.reduce((prev, curr) => {
			return prev + curr.cameras?.length ?? 0;
		}, 0);

		let progress = (this.currentReport.camerasComplete / totalCameraCount) * 100;

		return progress;
	}

	/**
	 * Returns true if the user can still edit report settings (report ID).
	 * If the user has progressed to the point where they've ran the report then this will return false.
	 */
	public get canEditReportSettings() {
		return (
			this.currentState == VideoReportingStates.PreSearch ||
			this.currentState == VideoReportingStates.Searching ||
			this.currentState == VideoReportingStates.ReviewingCameras
		);
	}

	/**
	 * Returns true if the user can still edit camera options. If the user has already
	 * performed their camera search, this will return false.
	 */
	public get canEditCameraSettings() {
		return this.currentState == VideoReportingStates.PreSearch;
	}

	/**
	 * Returns true if the user can edit their search. Used to disable the search components
	 * whilst we wait for the data service to return search results.
	 */
	public get canEditReportSearch() {
		return this.currentState == VideoReportingStates.PreSearch;
	}

	/**
	 * Returns true if the user is searching for nearby cameras as part of their report.
	 */
	public get searchingNearbyCameras() {
		return ["both", "nearby"].includes(this.cameraSearch);
	}

	/**
	 * Fetches the reporting prefs set in the system and assigns the max camera/distance defaults.
	 */
	public async created() {
		// Ensure when page is loaded it should reset back to starting state if not already
		if (this.currentState != "pre-search") {
			this.resetState();
		}

		await this.reset();
	}

	private async reset() {
		this.showCompleteReportModalFlag = false;

		await this.fetchVideoReportingPrefs();

		this.videoReportRequest = {
			videoReportId: null,
			cameraSettings: {
				includeLinkedCameras: false,
				maxCameras: this.maxCamerasLimit,
				maxDistance: this.maxDistanceLimit,
				maxDuration: this.maxDurationLimit,
			},
			search: null,
		};
	}

	/**
	 * Triggers the camera search using the report request, if the request is valid.
	 */
	private async search() {
		if (this.$v.videoReportRequest.search.$invalid) {
			return;
		}

		// Set camera search settings
		if (this.cameraSearch == "both") {
			this.videoReportRequest.cameraSettings.includeLinkedCameras = true;
		} else if (this.cameraSearch == "nearby") {
			this.videoReportRequest.cameraSettings.includeLinkedCameras = false;
		} else if (this.cameraSearch == "linked") {
			this.videoReportRequest.cameraSettings.includeLinkedCameras = true;
			this.videoReportRequest.cameraSettings.maxDistance = 0;
		}

		this.videoReportRequest = await videoReportingApi.search(this.videoReportRequest);
		this.setVideoReportingState(VideoReportingStates.ReviewingCameras);
	}

	/**
	 * Triggers the creation and processing of this video report, if the request is valid.
	 */
	private async runReport() {
		if (this.$v.$invalid) {
			return;
		}

		// Assign a handler to the onbeforeunload event. This will make it so that if the user
		// attempts to navigate away from the page while the report is running, they are
		// prompted by the browser to confirm they want to navigate away.
		window.onbeforeunload = function () {
			return true;
		};

		// Remove any devices from the report that don't have any associated cameras
		this.videoReportRequest.search = this.videoReportRequest.search.filter(
			(deviceSearch) => deviceSearch.cameras.length > 0
		);

		let requestResults = await this.processReport(this.videoReportRequest);
		this.setVideoReportingState(VideoReportingStates.CompletedReport);

		window.onbeforeunload = null;
	}

	/**
	 * Cancels this report and takes us back to the initial state.
	 */
	private cancelReview() {
		this.setVideoReportingState(VideoReportingStates.PreSearch);
	}

	/**
	 * Resets the video report store state and hides the complete modal.
	 */
	private async complete() {
		this.resetState();
		await this.reset();
	}
}
